import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth.jsx';
import { ageDepuisNaissance } from '../lib/format.js';

/** Aujourd'hui en « AAAA-MM-JJ », borne haute du champ de naissance. */
const aujourdHui = () => new Date().toISOString().slice(0, 10);

/**
 * Écran de première connexion.
 *
 * Les préférences n'existaient que sur un écran qu'il fallait penser à ouvrir :
 * personne ne les remplissait, et l'organisateur ajouté d'office comme voyageur
 * partait donc sans âge. On pose la question une fois, au moment où l'on vient
 * de créer son compte et où l'on est déjà en train de renseigner des choses.
 *
 * Les deux champs restent facultatifs et « Plus tard » est visible : ce sont
 * des commodités, pas un péage. Dans les deux cas l'écran est marqué comme vu,
 * et la question ne revient jamais.
 */
export function Bienvenue() {
  const { user, enregistrerPreferences } = useAuth();
  const naviguer = useNavigate();

  const [champs, setChamps] = useState({
    birthDate: user.birthDate ?? '',
    city: user.city ?? '',
  });
  const [erreur, setErreur] = useState(null);
  const [envoi, setEnvoi] = useState(false);

  // Déjà vu : on n'y revient pas, même en tapant l'adresse à la main.
  if (user.onboardingSeenAt) return <Navigate to="/voyages" replace />;

  const modifier = (champ) => (evenement) => {
    setErreur(null);
    setChamps((precedent) => ({ ...precedent, [champ]: evenement.target.value }));
  };

  const age = ageDepuisNaissance(champs.birthDate);

  async function terminer(preferences) {
    setErreur(null);
    setEnvoi(true);

    try {
      // `onboardingSeen` part avec la saisie, dans la même requête : deux
      // appels séparés laisseraient l'écran revenir si le second échouait.
      await enregistrerPreferences({ ...preferences, onboardingSeen: true });
      naviguer('/voyages', { replace: true });
    } catch (echec) {
      setErreur(echec.message);
      setEnvoi(false);
    }
  }

  return (
    <main className="page page--etroite">
      <section className="verre">
        <h1>Faisons connaissance, {user.firstName}</h1>
        <p className="aide aide--haut">
          Deux questions, une seule fois. Les réponses serviront de valeurs par
          défaut à chacun de vos voyages — jamais de contraintes : ce que vous
          saisirez dans un formulaire l’emportera toujours.
        </p>

        <form
          onSubmit={(evenement) => {
            evenement.preventDefault();
            terminer({
              birthDate: champs.birthDate || null,
              city: champs.city.trim() || null,
            });
          }}
          noValidate
        >
          <div className="champ">
            <label htmlFor="birthDate">Votre date de naissance</label>
            <input
              id="birthDate"
              type="date"
              max={aujourdHui()}
              autoComplete="bday"
              value={champs.birthDate}
              onChange={modifier('birthDate')}
            />
            <p className="aide">
              Vous êtes compté d’office parmi les voyageurs : votre âge affine
              le rythme des journées proposées.
              {age !== null && ` Vous avez ${age} ans.`}
            </p>
          </div>

          <div className="champ">
            <label htmlFor="city">Votre ville</label>
            <input
              id="city"
              type="text"
              autoComplete="address-level2"
              placeholder="Toulouse"
              value={champs.city}
              onChange={modifier('city')}
            />
            <p className="aide">Pré-remplit la ville de départ d’un nouveau voyage.</p>
          </div>

          {erreur && (
            <p className="erreur" role="alert">
              {erreur}
            </p>
          )}

          <div className="actions">
            <button className="bouton" type="submit" disabled={envoi}>
              {envoi ? 'Enregistrement…' : 'Continuer'}
            </button>
          </div>
        </form>

        <div className="accueil__plus-tard">
          <button
            className="lien-action"
            type="button"
            onClick={() => terminer({})}
            disabled={envoi}
          >
            Plus tard
          </button>
        </div>
      </section>
    </main>
  );
}
